import {
    REFERENCE_PROJECTS_DECISION_WITH,
    REFERENCE_PROJECTS_DECISION_WITHOUT,
} from './parseReply';
import type { ReferenceProjectsDecision } from './parseReply';

export type ReferenceProjectsOptions = {
    title?: string;
    /** Pre-selects the step-1 decision when the assistant already knows the user's intent. */
    preselectedDecision?: ReferenceProjectsDecision;
    /** Pre-selects specific work IDs in the picker (implies the WITH decision). */
    preselectedWorkIds?: string[];
};

function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/**
 * Parses the assistant-side reference-projects question payload that drives the composer selector.
 *
 * Every field is optional, so an empty object is valid and only a non-record returns `null` — the
 * live payload is frequently just `{}`, and rejecting it would silently drop the selector from the
 * composer. Unlike `parseStyleTilesOptions`, a malformed *individual* field is dropped rather than
 * failing the whole payload, matching the pilot's `validate` so a bad `preselectedDecision` still
 * renders a usable step 1.
 *
 * @param data - The input to parse (expected to be the assistant's `component.data`)
 * @returns A validated `ReferenceProjectsOptions`, or `null` if `data` is not an object
 */
export function parseReferenceProjectsOptions(data: unknown): ReferenceProjectsOptions | null {
    if (!isRecord(data)) {
        return null;
    }

    const { title, preselectedDecision, preselectedWorkIds } = data;

    const validTitle = typeof title === 'string' && title !== '' ? title : undefined;

    const validDecision =
        preselectedDecision === REFERENCE_PROJECTS_DECISION_WITH ||
        preselectedDecision === REFERENCE_PROJECTS_DECISION_WITHOUT
            ? preselectedDecision
            : undefined;

    const validWorkIds =
        Array.isArray(preselectedWorkIds) &&
        preselectedWorkIds.every((id) => typeof id === 'string')
            ? (preselectedWorkIds as string[])
            : undefined;

    return {
        ...(validTitle !== undefined ? { title: validTitle } : {}),
        ...(validDecision !== undefined ? { preselectedDecision: validDecision } : {}),
        ...(validWorkIds !== undefined ? { preselectedWorkIds: validWorkIds } : {}),
    };
}
